// client/src/components/pages/LexiStar/Routes/GeometryTools.jsx
//
// Where a route's line comes from (FR-ROUT-002): generated by the routing
// engine with a truck profile — drag-to-adjust by adding via points on the map —
// or a golden run from a finished trip (map-matched, simplified, then trimmed).
// Nothing is saved here: both produce a draft line for the editor.

import React, { useEffect, useMemo, useState } from 'react';
import { FaMagic, FaHistory, FaMapMarkerAlt, FaTimes, FaInfoCircle } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';
import UnifiedDateTimeRangePicker from '../../../common/UnifiedDateTimeRangePicker';
import { formatAppDateTime } from '../../../../utils/dateUtils';
import { routeFromTrip, listFinishedTrips } from '../../../../services/lexistarRoutes';
import { trimLine, lineKm, formatKm, formatMinutes } from './routeGeometry';

const inputClass =
  'w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

const apiError = (err, fallback) => err?.response?.data?.error || fallback;

/** Positive number or undefined (empty inputs are not sent). */
const num = (v) => {
  const n = Number(v);
  return v === '' || v == null || !Number.isFinite(n) || n <= 0 ? undefined : n;
};

/** 'YYYY-MM-DDTHH:MM' (local, from the shared picker) -> ISO instant. */
const localToIso = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

function EngineNote({ engine, note }) {
  if (!note && engine !== 'fake') return null;
  return (
    <p className="flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
      <FaInfoCircle className="mt-0.5 flex-shrink-0" aria-hidden="true" />
      <span>{note || 'Straight-line estimate: no routing engine on this server.'}</span>
    </p>
  );
}

// ── Generate by engine ─────────────────────────────────────────────────────

/** The generate request body for a lane, via points, end-point overrides and a truck profile. */
export function generateRequest(lane, { via = [], points = {}, vehicle = {} } = {}) {
  return {
    ...(points.from ? { from_point: points.from } : { from_location_id: lane.from_location_id }),
    ...(points.to ? { to_point: points.to } : { to_location_id: lane.to_location_id }),
    via,
    vehicle: {
      class: (vehicle.class || '').trim() || 'truck',
      height_m: num(vehicle.height_m),
      weight_t: num(vehicle.weight_t),
      axle_load_t: num(vehicle.axle_load_t),
    },
  };
}

export const DEFAULT_VEHICLE = { class: 'truck', height_m: '', weight_t: '', axle_load_t: '' };

/**
 * Presentational: the editor owns the state (so dragging a via point on the
 * map regenerates at once) and passes `onGenerate`.
 */
export function GeneratePanel({
  vehicle,
  onVehicleChange,
  busy,
  error,
  last,
  onGenerate,
  via,
  onViaChange,
  addingVia,
  onToggleAddingVia,
  points,
  pointMode,
  onPointMode,
  onClearPoints,
}) {
  const run = () => onGenerate();
  const setV = (k) => (e) => onVehicleChange({ ...vehicle, [k]: e.target.value });

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-slate-600">
          Vehicle class
          <input
            className={inputClass}
            value={vehicle.class}
            onChange={setV('class')}
            maxLength={40}
          />
        </label>
        <label className="text-xs text-slate-600">
          Height (m)
          <input
            className={inputClass}
            inputMode="decimal"
            value={vehicle.height_m}
            onChange={setV('height_m')}
            placeholder="4.3"
          />
        </label>
        <label className="text-xs text-slate-600">
          Gross mass (t)
          <input
            className={inputClass}
            inputMode="decimal"
            value={vehicle.weight_t}
            onChange={setV('weight_t')}
            placeholder="56"
          />
        </label>
        <label className="text-xs text-slate-600">
          Axle load (t)
          <input
            className={inputClass}
            inputMode="decimal"
            value={vehicle.axle_load_t}
            onChange={setV('axle_load_t')}
            placeholder="9"
          />
        </label>
      </div>

      <div className="rounded-lg border border-slate-200 p-2.5 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-slate-700">
            Via points{via.length ? ` (${via.length})` : ''}
          </p>
          <button
            type="button"
            onClick={onToggleAddingVia}
            aria-pressed={addingVia}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
              addingVia
                ? 'bg-blue-700 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <FaMapMarkerAlt size={10} aria-hidden="true" />
            {addingVia ? 'Done adding' : 'Add on map'}
          </button>
        </div>
        {via.length === 0 ? (
          <p className="text-xs text-slate-500">
            Add via points to steer the engine, then drag them to adjust.
          </p>
        ) : (
          <ol className="space-y-1">
            {via.map((p, i) => (
              <li
                key={`${p[0]},${p[1]}`}
                className="flex items-center justify-between text-xs text-slate-600"
              >
                <span className="tabular-nums">
                  {i + 1}. {p[1].toFixed(5)}, {p[0].toFixed(5)}
                </span>
                <button
                  type="button"
                  onClick={() => onViaChange(via.filter((_, j) => j !== i))}
                  aria-label={`Remove via point ${i + 1}`}
                  className="text-slate-400 hover:text-red-600"
                >
                  <FaTimes size={10} />
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-slate-500">Ends:</span>
        {['from', 'to'].map((end) => (
          <button
            key={end}
            type="button"
            onClick={() => onPointMode(pointMode === end ? null : end)}
            aria-pressed={pointMode === end}
            className={`px-2 py-1 rounded-md font-medium ${
              pointMode === end
                ? 'bg-blue-700 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {points[end]
              ? `${end === 'from' ? 'A' : 'B'} set on map`
              : `Set ${end === 'from' ? 'A' : 'B'} on map`}
          </button>
        ))}
        {(points.from || points.to) && (
          <button type="button" onClick={onClearPoints} className="text-slate-500 underline">
            use site locations
          </button>
        )}
      </div>

      {error && (
        <p
          role="alert"
          className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5"
        >
          {error}
        </p>
      )}
      {last && <EngineNote engine={last.engine} note={last.engine_note} />}

      <button
        type="button"
        onClick={run}
        disabled={busy}
        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-blue-700 rounded-lg hover:bg-blue-800 disabled:opacity-60"
      >
        <FaMagic size={12} aria-hidden="true" />
        {busy ? 'Routing…' : via.length ? 'Regenerate through via points' : 'Generate route'}
      </button>
    </div>
  );
}

// ── Create from trip (golden run) ───────────────────────────────────────────

export function FromTripPanel({ lane, onTrack, onResult }) {
  const [mode, setMode] = useState('trip');
  const [trips, setTrips] = useState([]);
  const [tripId, setTripId] = useState('');
  const [registration, setRegistration] = useState('');
  const [windowFrom, setWindowFrom] = useState('');
  const [windowTo, setWindowTo] = useState('');
  const [result, setResult] = useState(null);
  const [trim, setTrim] = useState([0, 100]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    listFinishedTrips().then((rows) => {
      if (!alive) return;
      setTrips(rows);
      if (!rows.length) setMode('vehicle');
    });
    return () => {
      alive = false;
    };
  }, []);

  // Trips on this lane first.
  const tripOptions = useMemo(() => {
    const onLane = (t) =>
      (t.legs || []).some(
        (l) =>
          l.kind === 'loaded' &&
          Number(l.from_location_id) === Number(lane.from_location_id) &&
          Number(l.to_location_id) === Number(lane.to_location_id)
      );
    return [...trips].sort((a, b) => Number(onLane(b)) - Number(onLane(a)));
  }, [trips, lane]);

  const apply = (res, [a, b]) => {
    const full = res.geometry.coordinates;
    const coords = trimLine(full, a / 100, b / 100);
    const fraction = (b - a) / 100;
    onResult({
      geometry: { type: 'LineString', coordinates: coords },
      planned_km: lineKm(coords),
      planned_min: Math.max(1, Math.round((res.planned_min || 0) * fraction)),
      source: 'golden_run',
      source_trip_id: res.source_trip_id || null,
      engine: res.engine,
    });
  };

  const load = async () => {
    setBusy(true);
    setError(null);
    try {
      const body =
        mode === 'trip'
          ? { trip_id: tripId }
          : {
              registration: registration.trim(),
              from: localToIso(windowFrom),
              to: localToIso(windowTo),
            };
      if (mode === 'trip' && !tripId)
        throw Object.assign(new Error(), { friendly: 'Choose a trip.' });
      if (mode === 'vehicle' && (!body.registration || !body.from || !body.to)) {
        throw Object.assign(new Error(), { friendly: 'Enter a registration and a time window.' });
      }
      const res = await routeFromTrip(body);
      setResult(res);
      setTrim([0, 100]);
      onTrack(res.geometry.coordinates);
      apply(res, [0, 100]);
    } catch (err) {
      setError(err.friendly || apiError(err, 'Could not build a golden run from that track.'));
    } finally {
      setBusy(false);
    }
  };

  const onTrim = (i) => (e) => {
    const v = Number(e.target.value);
    const next =
      i === 0 ? [Math.min(v, trim[1] - 1), trim[1]] : [trim[0], Math.max(v, trim[0] + 1)];
    setTrim(next);
    if (result) apply(result, next);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-1 text-xs" role="group" aria-label="Golden run source">
        {[
          ['trip', 'A finished trip'],
          ['vehicle', 'A vehicle and time window'],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setMode(id)}
            aria-pressed={mode === id}
            className={`flex-1 px-2 py-1.5 rounded-md font-medium ${
              mode === id
                ? 'bg-slate-800 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'trip' ? (
        <div>
          <label htmlFor="golden-trip" className="block text-xs text-slate-600 mb-1">
            Trip
          </label>
          <ThemedSelect id="golden-trip" value={tripId} onChange={(e) => setTripId(e.target.value)}>
            <option value="">
              {trips.length ? 'Choose a finished trip…' : 'No finished trips yet'}
            </option>
            {tripOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {[
                  t.reference,
                  t.registration || t.asset_key,
                  t.completed_at && formatAppDateTime(t.completed_at),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </option>
            ))}
          </ThemedSelect>
        </div>
      ) : (
        <div className="space-y-2">
          <label className="block text-xs text-slate-600">
            Registration
            <input
              className={inputClass}
              value={registration}
              onChange={(e) => setRegistration(e.target.value)}
              placeholder="JX 12 KL GP"
              maxLength={32}
            />
          </label>
          <div>
            <p className="text-xs text-slate-600 mb-1">When it drove the lane (up to 48 h)</p>
            <UnifiedDateTimeRangePicker
              startValue={windowFrom}
              endValue={windowTo}
              onStartChange={setWindowFrom}
              onEndChange={setWindowTo}
              placeholder="Choose the time window"
            />
          </div>
        </div>
      )}

      {error && (
        <p
          role="alert"
          className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5"
        >
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={load}
        disabled={busy}
        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-slate-800 rounded-lg hover:bg-slate-900 disabled:opacity-60"
      >
        <FaHistory size={12} aria-hidden="true" />
        {busy ? 'Matching the track…' : 'Load track'}
      </button>

      {result && (
        <div className="rounded-lg border border-slate-200 p-2.5 space-y-2">
          <p className="text-xs text-slate-600">
            {result.points_total} fixes {formatAppDateTime(result.window?.from)} –{' '}
            {formatAppDateTime(result.window?.to)}; simplified to {result.points_simplified} points
            {result.matched_ratio != null
              ? `, ${Math.round(result.matched_ratio * 100)}% matched to roads`
              : ''}
            .
          </p>
          <EngineNote engine={result.engine} note={result.engine_note} />
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-600">
              Trim start ({trim[0]}%)
              <input
                type="range"
                min={0}
                max={99}
                value={trim[0]}
                onChange={onTrim(0)}
                className="w-full accent-blue-700"
              />
            </label>
            <label className="text-xs text-slate-600">
              Trim end ({trim[1]}%)
              <input
                type="range"
                min={1}
                max={100}
                value={trim[1]}
                onChange={onTrim(1)}
                className="w-full accent-blue-700"
              />
            </label>
          </div>
          <p className="text-xs text-slate-500">
            Full track {formatKm(result.planned_km)}, {formatMinutes(result.planned_min)} — trim the
            yard manoeuvres at either end so the route starts and ends at the sites.
          </p>
        </div>
      )}
    </div>
  );
}
